import { RATING_DISPLAY_OFFSET, RATING_DISPLAY_SCALE } from '@topmeeps/shared'

// A compact, fixed-axis confidence bar — a forest plot with graded CI
// bands at the canonical reporting intervals. Every bar lives on the
// same 0–1000 SR axis (the display scale is built for this — see
// shared/src/rating/display.ts), so player marks are comparable across
// rows and across games.
//
// Renders two nested confidence intervals at ±1σ (~68%) and ±2σ (~95%
// — the universal "the CI" in statistics and reporting), plus an
// opaque vertical tick at μ as the point-estimate marker. Hard edges
// throughout — smooth gradients at this scale read as "out of focus."
//
// Use this on densely-packed contexts like group Standings. For solo
// cards where the bell shape itself is part of the story (Profile,
// OtherProfile), use RatingGaussian.

export type RatingBarProps = {
  // Raw OpenSkill μ (default ≈25).
  mu: number
  // Raw OpenSkill σ (default ≈8.33, stabilizes around 1.5–2.5).
  sigma: number
  // Fixed display-axis bounds. Default [0, 1000] from the SR scale.
  axisMin?: number
  axisMax?: number
  // Pixel dimensions of the track. Track is full width of its parent
  // up to maxWidth; the bands are positioned within.
  width?: number
  height?: number
  // Color of the bands. Defaults to felt.
  fill?: string
}

// Canonical confidence intervals: ±1σ (~68%) and ±2σ (~95%). The core
// band carries the "most likely" weight; the outer extends to the
// universal 95% CI. Symmetric around μ.
const BANDS: ReadonlyArray<{ from: number; to: number; opacity: number }> = [
  { from: -2, to: -1, opacity: 0.4 },
  { from: -1, to: 1, opacity: 0.85 },
  { from: 1, to: 2, opacity: 0.4 },
]

const displayMu = (mu: number) => mu * RATING_DISPLAY_SCALE + RATING_DISPLAY_OFFSET
const displaySigma = (sigma: number) => sigma * RATING_DISPLAY_SCALE

export const RatingBar = ({
  mu,
  sigma,
  axisMin = 0,
  axisMax = 1000,
  width = 160,
  height = 14,
  fill = 'var(--felt)',
}: RatingBarProps) => {
  const dMu = displayMu(mu)
  const dSigma = displaySigma(sigma)
  const axisSpan = axisMax - axisMin

  return (
    <div
      aria-hidden
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: width,
        height,
      }}
    >
      {/* Axis track — full 0–1000 line so the band's position is readable. */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: '50%',
          height: 1,
          background: 'var(--line-soft)',
          transform: 'translateY(-50%)',
        }}
      />
      {/* Stepped confidence bands. */}
      {BANDS.map((band, i) => {
        const lo = Math.max(axisMin, dMu + band.from * dSigma)
        const hi = Math.min(axisMax, dMu + band.to * dSigma)
        if (hi <= lo) return null
        const leftPct = ((lo - axisMin) / axisSpan) * 100
        const widthPct = ((hi - lo) / axisSpan) * 100
        return (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: bands are a fixed-length symmetric set
            key={i}
            style={{
              position: 'absolute',
              left: `${leftPct}%`,
              width: `${widthPct}%`,
              top: 0,
              bottom: 0,
              background: fill,
              opacity: band.opacity,
            }}
          />
        )
      })}
      {/* Point-estimate marker at μ. The forest-plot convention. */}
      <div
        style={{
          position: 'absolute',
          left: `calc(${((Math.max(axisMin, Math.min(axisMax, dMu)) - axisMin) / axisSpan) * 100}% - 1px)`,
          top: 0,
          bottom: 0,
          width: 2,
          background: fill,
        }}
      />
    </div>
  )
}
